<script lang="ts">
  import { getContext } from "svelte"
  import { portal } from "svelte-portal"
  import { parseWidgets } from "website_script/lib/Parsers"
  import getWidgetSelectors from "website_script/lib/getWidgetSelectors"
  import InlineButton from "./InlineButton.svelte"
  import InlineForm from "./InlineForm.svelte"
  import PriceAlertWidget from "./PriceAlertWidget.svelte"
  import SimilarCarsWidget from "./SimilarCarsWidget.svelte"
  import ModuleTypes from "../constants/ModuleTypes"
  import { getTestDriveModuleType } from "website_script/lib/getTestDriveModuleType"
  import type { Settings } from "website_script/types/settings"
  import type { DealerTheme } from "conversion_flows/shared/contexts/dealerTheme"
  import { getDealerThemeTokensStyle } from "website_script/lib/dealerTheme"
  import { FlowType } from "conversion_flows/types/scalars"
  import ConversionBox from "./ConversionBox.svelte"

  // Fetch global settings and texts labels from context
  const settings = getContext<Settings>("settings")
  const {
    enableConversionBox,
    enableSimilarCarsWidget,
    leadFlows,
    selectorToAppendConversionBox,
    selectorToAppendPriceAlertWidget,
    selectorToAppendPriceAlertCta,
    inlineButtonsConfig,
  } = settings
  const dealerTheme = getContext<DealerTheme>("dealerTheme")

  // Define the module type for the test drive
  $: testDriveModuleType = getTestDriveModuleType(settings)

  const customWidgetSelectors = {
    conversionBox: selectorToAppendConversionBox,
    priceAlertWidget: selectorToAppendPriceAlertWidget,
    priceAlertCtaClassName: selectorToAppendPriceAlertCta,
  }
  const widgets = parseWidgets(
    customWidgetSelectors,
    leadFlows?.inlineButtonSelectors
  )
  const widgetSelectors = getWidgetSelectors(customWidgetSelectors)

  const buttonConfigForLeadFlow = (flowType: FlowType, leadFlowId?: number) => {
    if (leadFlowId !== undefined) {
      const buttonByLeadFlowId = inlineButtonsConfig.buttons.find(
        (button) => button.lead_flow_id === leadFlowId
      )
      if (buttonByLeadFlowId) {
        return buttonByLeadFlowId
      }
    }
    const buttonByFlowType = inlineButtonsConfig.buttons.find(
      (button) => button.flow_type === flowType
    )

    if (buttonByFlowType) {
      return buttonByFlowType
    } else {
      console.error(
        `Did not find button config for flow with type ${flowType} ${
          leadFlowId ? `and lead flow id ${leadFlowId}` : ""
        }`
      )

      return null
    }
  }
</script>

<!--
@component

Searches for occurrences of html classes defined in ../lib/getWidgetSelectors,
or in the website script rules interface.

On those classes we will append different inline widgets, dependent on the class.

This is done automatically for some inline widgets, but others needs to be enabled.
-->
{#each widgets as widget}
  <div
    use:portal={widget.elTarget}
    style={getDealerThemeTokensStyle(dealerTheme)}
  >
    {#if widget.type === widgetSelectors.inlineCtaClassName}
      <InlineButton
        moduleType={ModuleTypes.TRADE_IN}
        buttonConfig={buttonConfigForLeadFlow("trade_in")}
        elementData={widget.elData}
      />
    {:else if widget.type === widgetSelectors.bookTestDriveCtaClassName}
      <InlineButton
        moduleType={testDriveModuleType}
        buttonConfig={buttonConfigForLeadFlow("test_drive")}
        elementData={widget.elData}
      />
    {:else if widget.type === widgetSelectors.inlineFormClassName}
      <InlineForm
        moduleType={ModuleTypes.TRADE_IN}
        elementData={widget.elData}
        on:flowEvent
      />
    {:else if widget.type === widgetSelectors.bookTestDriveFormClassName}
      <InlineForm
        moduleType={testDriveModuleType}
        elementData={widget.elData}
        on:flowEvent
      />
    {:else if enableConversionBox && widget.type === widgetSelectors.conversionBox}
      <ConversionBox elementData={widget.elData} />
    {:else if enableSimilarCarsWidget && widget.type === widgetSelectors.similarCarsWidgetClassName}
      <SimilarCarsWidget elementData={widget.elData} />
    {:else if widget.type === widgetSelectors.priceAlertWidget && settings.car && settings.car.price}
      <PriceAlertWidget elementData={widget.elData} />
    {:else if widget.type === widgetSelectors.priceAlertCtaClassName && settings.car && settings.car.price}
      <InlineButton
        moduleType={ModuleTypes.PRICE_ALERT}
        buttonConfig={buttonConfigForLeadFlow("price_alert")}
        elementData={widget.elData}
      />
    {:else if widget.leadFlowId && buttonConfigForLeadFlow("custom", widget.leadFlowId)}
      <InlineButton
        moduleType={ModuleTypes.CUSTOM}
        buttonConfig={buttonConfigForLeadFlow("custom", widget.leadFlowId)}
        elementData={widget.elData}
        leadFlowId={widget.leadFlowId}
      />
    {/if}
  </div>
{/each}
